import { ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { GridApiPro, GridRowParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { MutableRefObject, useCallback } from 'react';
import { Union } from 'ts-toolbelt';

import { TsrctSourcingCaseQuote } from '@/generated/gql/graphql';
import { NegotiationDomainType } from '~/common/types';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';
import { RowDetailPanel } from '~/components/DataGridCustom/components/RowDetailPanel/RowDetailPanel';

import { QuoteLineItemGridField, useSourcingCaseQuoteDataGridConfig } from './SourcingCaseQuote.dataGrid';

export interface NegotiatedQuoteDetailsProps {
  data?: Union.Nullable<TsrctSourcingCaseQuote>;
  loading?: boolean;
  viewOnly?: boolean;
  gridApiRef: MutableRefObject<GridApiPro>;
}
const domain = NegotiationDomainType.SOURCING_CASE_QUOTE;
export const SourcingCaseQuote = ({ data, loading, viewOnly = true, gridApiRef }: NegotiatedQuoteDetailsProps) => {
  const config = useSourcingCaseQuoteDataGridConfig(viewOnly);

  const getDetailPanelContent = useCallback(<R extends GridValidRowModel = any>({ row }: GridRowParams<R>) => {
    return <RowDetailPanel description={row.description} />;
  }, []);

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  const { gridProps, onRemoteStateReset } = useDataGridRemoteState({
    apiRef: gridApiRef,
    config,
    domain,
  });

  return (
    <>
      <DataGridWidgetHeader
        disablePadding
        right={
          <Stack direction="row">
            <ColumnSelector
              columns={gridProps.columns}
              columnGroupingModel={gridProps.columnGroupingModel}
              columnVisibilityModel={gridProps.columnVisibilityModel}
              onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
            />
            <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
          </Stack>
        }
      />
      <DataGridProStyled
        {...config}
        initialState={{
          pinnedColumns: { right: [QuoteLineItemGridField.STATUS] },
        }}
        apiRef={gridApiRef}
        autoHeight
        hideFooter
        disableColumnMenu
        loading={loading}
        rows={data?.lineItems ?? []}
        getDetailPanelHeight={getDetailPanelHeight}
        getDetailPanelContent={getDetailPanelContent}
        slots={{
          detailPanelExpandIcon: ChevronRightOutlined,
          detailPanelCollapseIcon: ExpandMoreOutlined,
        }}
        sx={{
          '& .MuiDataGrid-row': { cursor: 'default' },
        }}
        rowSelection={false}
        disableRowSelectionOnClick
        processRowUpdate={viewOnly ? undefined : (newRow) => newRow}
      />
    </>
  );
};
